import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'

import { RadioButton } from '@/components/atoms/radio-button'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'
import { usePrinterSetting } from '@/hooks/self-checkin-device-settings/use-printer-setting'
import { DeviceSettingsModalFrame, DeviceSettingsModalNavigationProps } from './modal-frame'

type Props = DeviceSettingsModalNavigationProps & {
  hotelId: string
}

export const PrinterDeviceSettingsPanel: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { isLoading, printerSettings, selectedId, selectPrinter } = usePrinterSetting(props.hotelId, { isSelfCheckinTablet: true })

  return (
    <DeviceSettingsModalFrame {...props} isLoading={isLoading} isDirty={false} onCancel={() => {}} onSave={() => {}}>
      <div css={headerContainerStyle}>
        {t('Please make selections on the tablet (iPad) to be used to connect the printer.')}
        <br />
        {t('When using multiple tablets/printers, please set up each tablet individually.')}
      </div>
      <div css={mainContainerStyle}>
        {printerSettings.map(device => (
          <div key={device.id || 'empty'} css={printerRowStyle}>
            <RadioButton
              id={device.id || 'empty'}
              checked={selectedId === device.id}
              item={{ value: device.id, label: device.deviceName }}
              onChange={() => selectPrinter(device)}
              style={{ radioSize: 40, labelMargin: 24 }}
              itemCss={printerRadioStyle}
            />
            {device.deviceHost && (
              <a target="_blank" rel="noreferrer" href={`https://${device.deviceHost}`} css={webConfigLinkStyle}>
                web configを開く
              </a>
            )}
          </div>
        ))}
      </div>
    </DeviceSettingsModalFrame>
  )
}

const headerContainerStyle = css(
  {
    boxSizing: 'border-box',
    padding: '32px 40px',
    borderBottom: '1px solid #CCC',
    display: 'flex',
    alignItems: 'center',
    color: '#676767',
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 500,
    letterSpacing: '0.8px',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      fontSize: 20,
      lineHeight: '30px',
      letterSpacing: '1px',
    },
  }),
)

const mainContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  color: '#272727',
  width: '100%',
  flexWrap: 'wrap',
})

const printerRowStyle = css(
  {
    height: 76,
    borderBottom: '1px solid #CCC',
    padding: '0 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  createSelfCheckinResponsiveStyle({ landscape13: { height: 88 } }),
)

const printerRadioStyle = css(
  {
    flexGrow: 1,
    height: 76,
    '> label': {
      fontSize: 20,
      fontWeight: 500,
      letterSpacing: '1px',
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      height: 88,
      '> label': {
        fontSize: 24,
        letterSpacing: '1.2px',
      },
    },
  }),
)

const webConfigLinkStyle = css({ color: '#f2a40b' })
